import React from 'react';
import axios from 'axios';
import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import CliveActionBanner from '~/components/Clive/CliveBanner/CliveActionBanner';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import SwitchFormik from '~/components/core/Formik/SwitchFormik';
import DisableableTooltip from '~/components/DisableableTooltip';
import { useCliveConfigOptions } from '~/components/SystemConfiguration/CliveConfiguration/CliveConfigurationContext';
import TextFieldFormik from '~/components/TextFieldFormik';
import { reportAxiosError } from '~/Utils';

const getFnolInboxConfigUrl = (organization: number, lobName: string, subOrg?: number | null) => {
  if (subOrg) {
    return `/api/v1/fnol_inbox/config/organizations/${organization}/sub_organizations/${subOrg}/lobs/${lobName}`;
  }
  return `/api/v1/fnol_inbox/config/organizations/${organization}/lobs/${lobName}`;
};
const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+$/;
const validationSchema = Yup.object().shape({
  emailName: Yup.string().when('activateClive', {
    is: true,
    then: (schema) =>
      schema
        .required('Email name is required when Clive is activated')
        .matches(EMAIL_REGEX, 'Invalid Email name format'),
  }),
  activateClive: Yup.boolean(),
});

interface SetInboxDialogProps {
  onClose: () => void;
  domain?: string | null;
  organization: number;
  lobName: string;
  subOrg?: number | null;
  emailName: string;
  activateClive: boolean;
}

interface FormValues {
  emailName: string;
  activateClive: boolean;
}

interface SetInboxDialogInnerProps {
  onClose: () => void;
  emailName: string;
  domain?: string | null;
  subOrg?: number | null;
}

const SetInboxDialogInner: React.FC<SetInboxDialogInnerProps> = ({ onClose, emailName, domain, subOrg }) => {
  const { handleSubmit, isSubmitting, values, setFieldValue } = useFormikContext<FormValues>();
  const dialogTitle = emailName ? 'Edit Inbox' : 'Set Inbox';

  const { isCliveEnabled } = useCliveConfigOptions();

  // Check if Clive is disabled for this organization/sub-organization
  const isCliveDisabled = !isCliveEnabled(subOrg);

  // Override the field value to false when Clive is disabled
  React.useEffect(() => {
    if (isCliveDisabled && values.activateClive) {
      setFieldValue('activateClive', false);
    }
  }, [isCliveDisabled, values.activateClive, setFieldValue]);

  return (
    <>
      <CardDialog
        title={dialogTitle}
        isDialog
        open={true}
        maxWidth="lg"
        onClose={onClose}
        footerActions={
          <DialogFooterActions
            onClickPrimary={handleSubmit}
            onClickSecondary={onClose}
            primaryLabel="Save"
            showPrimary
            secondaryLabel="Cancel"
            disabled={isSubmitting}
          />
        }
      >
        <div className="flex flex-col gap-24">
          <div className="flex w-full flex-row items-center gap-20">
            <TextFieldFormik
              label="Email Name"
              name="emailName"
              placeholder="Enter email name"
              id="emailName"
              className="w-full"
              helperText=" "
            />
            <TextFieldFormik
              label="Domain"
              name="domain"
              id="domain"
              value={'@' + domain}
              disabled={true}
              className="w-full"
              helperText=" "
            />
          </div>
          <CliveActionBanner
            title="Activate Clive for Email Analysis & FNOL Drafting"
            contentText="I'll review the email and extract the key details to create an initial FNOL draft"
            actionComponent={
              <DisableableTooltip disabled={isCliveDisabled} title="Enable Clive for your organization to begin">
                <SwitchFormik id="activateClive" label="" hideErrorGap={true} disabled={isCliveDisabled} />
              </DisableableTooltip>
            }
          />
        </div>
      </CardDialog>
    </>
  );
};

const SetInboxDialog: React.FC<SetInboxDialogProps> = ({
  onClose,
  domain,
  organization,
  subOrg,
  lobName,
  emailName,
  activateClive,
}) => {
  const handleSubmit = async (values: FormValues) => {
    try {
      await axios.put(getFnolInboxConfigUrl(organization, lobName, subOrg), {
        fnol_email_inbox_name: values.emailName.trim() ? values.emailName.trim() : null,
        should_send_fnol_email_inbox_to_clive: values.activateClive,
      });
      onClose();
    } catch (error) {
      await reportAxiosError(error);
    }
  };
  return (
    <Formik<FormValues>
      initialValues={{ emailName, activateClive }}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      <SetInboxDialogInner onClose={onClose} domain={domain} emailName={emailName} subOrg={subOrg} />
    </Formik>
  );
};

export default SetInboxDialog;
